import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToastService, DraftToast, ErrorToast, SuccessToast } from '../../services/toast.service';

@Component({
  selector: 'sp-toast',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Success: Draft saved -->
    <div class="sp-toast sp-toast--success" *ngIf="draftToast" role="status" aria-live="polite">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#329989"/>
        <path d="M6 10.5l3 3 5-5.5" stroke="#fff" stroke-width="1.8"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div class="sp-toast__body">
        <strong>{{ draftToast.label ?? 'Draft saved' }}</strong>
        <span>Plan ID: <code>{{ draftToast.planId }}</code></span>
      </div>
    </div>

    <!-- Generic success (e.g. feedback submitted) -->
    <div class="sp-toast sp-toast--success" *ngIf="successToast" role="status" aria-live="polite">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#329989"/>
        <path d="M6 10.5l3 3 5-5.5" stroke="#fff" stroke-width="1.8"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div class="sp-toast__body">
        <strong>{{ successToast.title }}</strong>
        <span>{{ successToast.message }}</span>
      </div>
    </div>

    <!-- Error -->
    <div class="sp-toast sp-toast--error" *ngIf="errorToast" role="alert" aria-live="assertive">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#e53e3e"/>
        <path d="M10 5v6M10 13.5v1.5" stroke="#fff" stroke-width="2"
              stroke-linecap="round"/>
      </svg>
      <div class="sp-toast__body">
        <strong>{{ errorToast.title ?? "Couldn't save" }}</strong>
        <span>{{ errorToast.message }}</span>
        <button *ngIf="errorToast.action as action"
                type="button"
                class="sp-toast__action"
                (click)="onErrorActionClick()"
                (mouseenter)="onErrorActionHoverStart()"
                (mouseleave)="onErrorActionHoverEnd()">
          {{ action.label }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .sp-toast {
      position: fixed;
      top: 24px;
      right: 24px;
      z-index: 9000;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 18px;
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.13);
      max-width: 360px;
      animation: spToastIn 0.22s ease;
      pointer-events: none;

      &--success { border: 1.5px solid #b7e8dc; }
      &--error   { border: 1.5px solid #fbb6b6; top: 80px; }

      &__body {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: 13px;
        color: #1a202c;

        strong { font-weight: 700; font-size: 13px; }
        span   { color: #718096; font-size: 12px; }
        code   { font-family: monospace; font-size: 12px; color: #208171; }
      }

      &__action {
        /* The only interactive part of the toast — everything else stays
           pointer-events: none (see .sp-toast above) so it never blocks the
           page underneath. Styled with the same structural pattern as the
           forecast card's inline retry control (border, radius, hover fill),
           reusing this toast's own error-red tokens — no new colors. */
        pointer-events: auto;
        align-self: flex-start;
        margin-top: 4px;
        padding: 4px 10px;
        font: inherit;
        font-size: 12px;
        font-weight: 700;
        color: #e53e3e;
        background: rgba(255, 255, 255, 0.6);
        border: 1px solid #fbb6b6;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.15s, border-color 0.15s;

        &:hover {
          background: #fff;
          border-color: #e53e3e;
        }
      }
    }

    @keyframes spToastIn {
      from { opacity: 0; transform: translateY(-10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `],
})
export class ToastComponent implements OnInit, OnDestroy {
  private static readonly ERROR_DISMISS_MS = 5000;

  draftToast:   DraftToast   | null = null;
  errorToast:   ErrorToast   | null = null;
  successToast: SuccessToast | null = null;
  private draftTimer:   ReturnType<typeof setTimeout> | null = null;
  private errorTimer:   ReturnType<typeof setTimeout> | null = null;
  private successTimer: ReturnType<typeof setTimeout> | null = null;
  private draftSub!:   Subscription;
  private errorSub!:   Subscription;
  private successSub!: Subscription;

  // Error-toast auto-dismiss pause/resume (only the action button is
  // hoverable — the toast body stays pointer-events: none). Tracks
  // remaining time rather than resetting it, so a hover-then-leave doesn't
  // hand the user a fresh 5s window.
  private errorRemainingMs  = ToastComponent.ERROR_DISMISS_MS;
  private errorTimerStarted = 0;

  constructor(
    private readonly toastSvc: ToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.draftSub = this.toastSvc.draft$.subscribe(msg => {
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftToast = msg;
      this.cdr.markForCheck();
      if (msg) {
        this.draftTimer = setTimeout(() => {
          this.draftToast = null;
          this.cdr.markForCheck();
        }, 3000);
      }
    });

    this.errorSub = this.toastSvc.error$.subscribe(msg => {
      if (this.errorTimer) clearTimeout(this.errorTimer);
      this.errorToast = msg;
      this.cdr.markForCheck();
      if (msg) {
        this.errorRemainingMs = ToastComponent.ERROR_DISMISS_MS;
        this.startErrorTimer();
      }
    });

    this.successSub = this.toastSvc.success$.subscribe(msg => {
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successToast = msg;
      this.cdr.markForCheck();
      if (msg) {
        this.successTimer = setTimeout(() => {
          this.successToast = null;
          this.cdr.markForCheck();
        }, 3500);
      }
    });
  }

  ngOnDestroy(): void {
    this.draftSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.successSub?.unsubscribe();
    if (this.draftTimer)   clearTimeout(this.draftTimer);
    if (this.errorTimer)   clearTimeout(this.errorTimer);
    if (this.successTimer) clearTimeout(this.successTimer);
  }

  /** Runs the toast's retry action (if any) then dismisses — click never leaves a stale toast. */
  onErrorActionClick(): void {
    const run = this.errorToast?.action?.run;
    this.dismissError();
    run?.();
  }

  /**
   * Pauses the auto-dismiss timer while the pointer is over the action
   * button, so the 5s window can't remove the button out from under a user
   * mid-click. Preserves the remaining time — a hover/unhover cycle never
   * grants a fresh 5s window.
   */
  onErrorActionHoverStart(): void {
    if (!this.errorTimer) return; // already paused or dismissed
    clearTimeout(this.errorTimer);
    this.errorTimer = null;
    const elapsed = Date.now() - this.errorTimerStarted;
    this.errorRemainingMs = Math.max(0, this.errorRemainingMs - elapsed);
  }

  onErrorActionHoverEnd(): void {
    if (!this.errorToast || this.errorTimer) return; // no toast, or not paused
    this.startErrorTimer();
  }

  private startErrorTimer(): void {
    this.errorTimerStarted = Date.now();
    this.errorTimer = setTimeout(() => {
      this.errorTimer = null;
      this.errorToast = null;
      this.cdr.markForCheck();
    }, this.errorRemainingMs);
  }

  private dismissError(): void {
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
    this.errorToast = null;
    this.cdr.markForCheck();
  }
}
